import { useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { forgetMyRememberedDevice } from "../../api/deviceKeys.js";
import type { RememberedDevice } from "../../api/deviceKeys.js";
import { ApiClientError } from "../../api/http.js";
import { REMEMBERED_DEVICES_KEY, SESSIONS_KEY, useMyRememberedDevices } from "../../data/account.js";
import { Alert, Section, Skeleton } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";
import { RememberedDeviceList } from "./RememberedDeviceList.js";

/** Installations that may sign in without the second factor; forgetting one revokes its key and signs it out. */
export function RememberedDevicesSection(): ReactNode {
  const devices = useMyRememberedDevices();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [forgetting, setForgetting] = useState<string | undefined>(undefined);

  async function forget(device: RememberedDevice): Promise<void> {
    setForgetting(device.id);
    try {
      await forgetMyRememberedDevice(device.id);
      toast.notify(`${device.deviceName} vergessen und abgemeldet.`, { tone: "success" });
    } catch (err: unknown) {
      toast.notify(err instanceof ApiClientError ? err.message : "Vergessen fehlgeschlagen.", { tone: "danger" });
    } finally {
      setForgetting(undefined);
      await queryClient.invalidateQueries({ queryKey: REMEMBERED_DEVICES_KEY });
      await queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
    }
  }

  if (devices.data !== undefined && devices.data.length === 0) {
    return null;
  }
  return (
    <Section
      title="Gemerkte Geräte"
      description="Diese Apps haben sich nach einer Anmeldung mit zweitem Faktor gemerkt und brauchen dort bis zum angegebenen Datum nur das Master-Passwort."
    >
      <div className="flex flex-col gap-3">
        {devices.isPending ? <Skeleton className="h-16" /> : null}
        {devices.error !== null ? <Alert tone="danger">{devices.error.message}</Alert> : null}
        {devices.data !== undefined ? (
          <RememberedDeviceList devices={devices.data} forgetting={forgetting} onForget={(d: RememberedDevice): void => void forget(d)} empty="Keine gemerkten Geräte." />
        ) : null}
        <p className="text-[0.8125rem] text-mist-2">
          Vergessen meldet das Gerät sofort ab. Danach braucht es bei der nächsten Anmeldung wieder den zweiten Faktor.
        </p>
      </div>
    </Section>
  );
}
